import { API_BASE, request } from '@/src/infrastructure/lib/api';

export type CanonicalAspectRatio = 'square' | 'portrait' | 'landscape' | 'story';

export interface AiStyle {
  id: string;
  occasion: string;
  name: string;
  promptTemplate: string;
  thumbnailUrl: string;
  defaultTextRenderMode: string;
  imageProvider?: string;
  /** Server-calculated provider capability; false means uploaded photos cannot affect likeness. */
  supportsReferenceConditioning?: boolean;
  maxFaces: number;
  active: boolean;
  /** Aspect ratios this style supports — admin-configurable. Defaults to ['square']. */
  allowedAspectRatios: CanonicalAspectRatio[];
  /** Pre-selected ratio in the user picker. Must be in allowedAspectRatios. */
  defaultAspectRatio: CanonicalAspectRatio;
  createdAt?: string;
}

export interface GenerateArtifactPayload {
  occasion: string;
  styleId: string;
  inputPhotoRefs?: string[];
  slots: Record<string, string>;
  invitationId?: string;
  textRenderMode?: string;
  dryRun?: boolean;
  /** Canonical aspect ratio. Must be in selectedStyle.allowedAspectRatios. Defaults to selectedStyle.defaultAspectRatio. */
  aspectRatio?: CanonicalAspectRatio;
  gender?: 'male' | 'female' | 'neutral';
  birthYear?: number;
}


export interface AiGenerationRecord {
  id: string;
  userId: string;
  invitationId?: string | null;
  occasion: string;
  styleId: string;
  facesUsed: boolean;
  inputPhotoRefs: string[];
  resultImageUrl?: string | null;
  status: 'PENDING' | 'MODERATED_REJECTED' | 'GENERATING' | 'READY' | 'SUCCESS' | 'FAILED';
  creditConsumptionId?: string | null;
  textRenderMode: string;
  aspectRatio?: CanonicalAspectRatio | string;
  slots?: Record<string, string>;
  createdAt: string;
  style?: AiStyle;
}

export async function fetchActiveAiStyles(occasion?: string): Promise<AiStyle[]> {
  const query = occasion ? `?occasion=${encodeURIComponent(occasion.toLowerCase())}` : '';
  const url = `${API_BASE}/ai/artifacts/styles${query}`;
  return request<AiStyle[]>(url, { method: 'GET' });
}

export async function generateAiArtifact(
  payload: GenerateArtifactPayload,
): Promise<AiGenerationRecord> {
  const url = `${API_BASE}/ai/artifacts/generate`;
  return request<AiGenerationRecord>(url, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getAiGenerationStatus(
  id: string,
): Promise<AiGenerationRecord> {
  const url = `${API_BASE}/ai/artifacts/${id}`;
  return request<AiGenerationRecord>(url, { method: 'GET' });
}

export async function fetchMyAiGenerations(): Promise<AiGenerationRecord[]> {
  const url = `${API_BASE}/ai/artifacts/my`;
  return request<AiGenerationRecord[]>(url, { method: 'GET' });
}
